// PRD.275 — Elia AI Assistant Drawer (Phase 1, UI only).
// PRD.279 — Voice dictation: a mic action that records, transcribes, and appends
// the editable transcript to the draft (never auto-sends).
//
// Fixed composer (FR-11): disabled plus affordance, auto-growing textarea, and
// send button. Enter sends, Shift+Enter inserts a newline, whitespace-only
// input cannot be sent.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ELIA_COLORS, ELIA_FONT } from './elia-theme';
import { isSendableText } from './elia-chat-reducer';
import { EliaCloseIcon, EliaMicIcon, EliaPlusIcon, EliaSendIcon, EliaStopIcon } from './EliaIcons';
import { appendTranscript } from './elia-voice-dictation';
import { useEliaVoiceDictation } from './useEliaVoiceDictation';

const MAX_TEXTAREA_HEIGHT = 120;

export function EliaComposer({
  onSend,
  busy = false,
  placeholder,
  contextKey,
}: {
  onSend: (text: string) => void;
  busy?: boolean;
  placeholder: string;
  /** Effective Elia context key; dictation is cancelled if it changes mid-op. */
  contextKey: string;
}) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingFocusRef = useRef(false);
  const canSend = isSendableText(value) && !busy;

  const dictation = useEliaVoiceDictation({
    contextKey,
    sending: busy,
    onInsertTranscript: (transcript) => {
      setValue((prev) => appendTranscript(prev, transcript));
      pendingFocusRef.current = true; // focus once the textarea re-mounts
    },
  });

  const isRecording = dictation.status === 'recording';
  const isTranscribing = dictation.status === 'transcribing';
  const showRecordingBar = isRecording || isTranscribing;

  // Auto-grow the textarea up to a capped height (only while it is mounted).
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el || showRecordingBar) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [value, showRecordingBar]);

  // Return focus to the composer after a transcript is inserted.
  useEffect(() => {
    if (!showRecordingBar && pendingFocusRef.current) {
      pendingFocusRef.current = false;
      textareaRef.current?.focus();
    }
  }, [showRecordingBar]);

  const submit = () => {
    if (!canSend) return;
    onSend(value);
    setValue('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const mm = Math.floor(dictation.elapsed / 60);
  const ss = String(dictation.elapsed % 60).padStart(2, '0');

  return (
    <div
      style={{
        borderTop: `1px solid ${ELIA_COLORS.borderSubtle}`,
        padding: '13px 12px 12px',
        fontFamily: ELIA_FONT,
      }}
    >
      {dictation.error ? (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginBottom: 8,
            padding: '8px 12px',
            borderRadius: 12,
            background: '#fef2f2',
            color: '#b91c1c',
            fontSize: 12,
            lineHeight: '16px',
          }}
        >
          <span style={{ flex: 1 }}>{dictation.error}</span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={dictation.dismissError}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#b91c1c',
              cursor: 'pointer',
              padding: 0,
              display: 'inline-flex',
              flexShrink: 0,
            }}
          >
            <EliaCloseIcon size={14} />
          </button>
        </div>
      ) : null}

      {showRecordingBar ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 36 }}>
          <span
            aria-live="polite"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: ELIA_COLORS.text }}
          >
            {isRecording ? (
              <>
                <span
                  aria-hidden="true"
                  style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc2626' }}
                />
                Recording… {mm}:{ss}
              </>
            ) : (
              'Transcribing…'
            )}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={dictation.cancel}
              style={{
                border: `1px solid ${ELIA_COLORS.border}`,
                background: 'transparent',
                color: ELIA_COLORS.text,
                borderRadius: 9999,
                padding: '6px 14px',
                fontFamily: ELIA_FONT,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            {isRecording ? (
              <button
                type="button"
                onClick={dictation.stop}
                style={{
                  border: 'none',
                  background: ELIA_COLORS.pink,
                  color: '#ffffff',
                  borderRadius: 9999,
                  padding: '6px 16px',
                  fontFamily: ELIA_FONT,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <EliaStopIcon size={12} />
                Stop
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <button
            type="button"
            aria-label="Add attachment (coming soon)"
            disabled
            style={circleButton(ELIA_COLORS.textFaint, 32, 'not-allowed')}
          >
            <EliaPlusIcon size={20} />
          </button>

          <textarea
            ref={textareaRef}
            value={value}
            rows={1}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            aria-label="Message Elia AI"
            style={{
              flex: 1,
              minWidth: 0,
              resize: 'none',
              border: 'none',
              outline: 'none',
              background: ELIA_COLORS.neutralSurface,
              borderRadius: 20,
              padding: '10px 16px',
              fontFamily: ELIA_FONT,
              fontSize: 14,
              lineHeight: '20px',
              letterSpacing: '-0.15px',
              color: ELIA_COLORS.text,
              maxHeight: MAX_TEXTAREA_HEIGHT,
              overflowY: 'auto',
            }}
          />

          {dictation.available ? (
            <button
              type="button"
              aria-label="Start voice dictation"
              title={dictation.canStart ? 'Start voice dictation' : 'Voice dictation unavailable right now'}
              onClick={dictation.start}
              disabled={!dictation.canStart}
              style={circleButton(
                ELIA_COLORS.pink,
                36,
                dictation.canStart ? 'pointer' : 'default',
                dictation.canStart ? 1 : 0.4,
              )}
            >
              <EliaMicIcon size={20} />
            </button>
          ) : null}

          <button
            type="button"
            aria-label="Send message"
            onClick={submit}
            disabled={!canSend}
            style={{
              ...circleButton('#ffffff', 36, canSend ? 'pointer' : 'default', canSend ? 1 : 0.4),
              background: ELIA_COLORS.pink,
            }}
          >
            <EliaSendIcon size={16} color="#ffffff" />
          </button>
        </div>
      )}
    </div>
  );
}

function circleButton(
  color: string,
  size: number,
  cursor: React.CSSProperties['cursor'],
  opacity = 1,
): React.CSSProperties {
  return {
    width: size,
    height: size,
    borderRadius: '9999px',
    border: 'none',
    background: 'transparent',
    color,
    opacity,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor,
    flexShrink: 0,
    padding: 0,
  };
}
